import { LogOut, UserRoundCheck } from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import { isTabSession } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ROLE_LABELS, type AppUser } from "@/lib/types";

/**
 * Across the top of the console while an admin is signed in as somebody else with a sign-in link
 * (ADR-040 in `platform`): whose console this is, who opened it, that the phone is off, and Exit.
 *
 * Exit ends the session. A tab that opened the link beside the admin's own session goes back to it,
 * on the Users page the link was made from; a browser whose only session this was goes to sign in.
 * Both are full page loads, so nothing of the person's session outlives the exit in memory.
 */
export function SignedInAsBar() {
  const { user, logout } = useAuth();
  const [leaving, setLeaving] = React.useState(false);
  if (!user?.signed_in_by) return null;

  const exit = async () => {
    setLeaving(true);
    const backToAdmin = isTabSession();
    try {
      await logout();
    } catch {
      // The session is gone from this tab either way; the page load below is what matters.
    }
    window.location.assign(backToAdmin ? "/users" : "/login");
  };

  return <SignedInAsBarView user={user} leaving={leaving} onExit={() => void exit()} />;
}

/** The bar itself, for the tests. */
export function SignedInAsBarView({
  user, leaving, onExit,
}: {
  user: AppUser;
  leaving: boolean;
  onExit: () => void;
}) {
  const by = user.signed_in_by;
  if (!by) return null;
  const name = user.display_name || user.email;
  return (
    <div
      role="region"
      aria-label="Signed in as somebody else"
      className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-warning-4 bg-warning-2 px-4 py-2 text-xs text-warning-11"
    >
      <UserRoundCheck className="size-4 shrink-0" aria-hidden />
      <p className="min-w-0 flex-1">
        You are signed in as <span className="font-semibold">{name}</span>
        {user.display_name && <span className="break-all"> ({user.email})</span>} ·{" "}
        {ROLE_LABELS[user.role]}. Opened by {by.display_name || by.email}. The phone is off.
      </p>
      <Button variant="outline" size="sm" loading={leaving} onClick={onExit}>
        <LogOut />
        Exit
      </Button>
    </div>
  );
}
